import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash';
import { SerializedAccountTracker } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../common/types';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../../common/views/LoadingView';
import { errorAlertMessage } from '../../../common/views/ErrorAlert/errorAlertMessage';
import { ConfirmDialog } from '../../../common/views/modals/confirmDialog/ConfirmDialog';
import {
  ConfirmDialogIconMark,
  ConfirmDialogTileMark,
} from '../../../common/views/modals/confirmDialog/ConfirmDialogMark';
import { useDeleteTrackerMutation, useGetTrackersQuery } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { TrackersPanel } from '../../../smartLinks/trackers/TrackersPanel';
import { AddTrackerModal } from '../../../smartLinks/trackers/AddTrackerModal';
import { trackerProviderMark } from '../../../smartLinks/trackers/trackerProviders';

/**
 * The trackers of the active artist (design artist-settings b1, b2). It replaces the old
 * Manage Trackers page: the same panel, the same add dialog and the same remove confirm
 * (design dialogs a8). A tracker saves on its own; the save bar of the pane is only for the
 * address.
 */
const TrackersSectionComponent: React.FC<{ accountId: number }> = ({ accountId }) => {
  const intl = useIntl();
  const { data, isLoading, error, refetch } = useGetTrackersQuery(accountId);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [trackerToRemove, setTrackerToRemove] = useState<SerializedAccountTracker | undefined>();
  const [deleteTracker, { error: deleteError, isLoading: isDeleting, reset: resetDelete }] = useDeleteTrackerMutation();

  const openAdd = useCallback(() => setIsAddOpen(true), []);
  const closeAdd = useCallback(() => setIsAddOpen(false), []);
  const closeRemove = useCallback(() => {
    setTrackerToRemove(undefined);
    resetDelete();
  }, [resetDelete]);
  const onRemove = useCallback(
    (tracker: SerializedAccountTracker) => {
      resetDelete();
      setTrackerToRemove(tracker);
    },
    [resetDelete]
  );
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  // The dialog stays open while the delete runs, and shows a failure inside it (a4, a5).
  const confirmRemove = useCallback(() => {
    if (trackerToRemove === undefined) {
      return;
    }
    deleteTracker({ accountId, trackerId: trackerToRemove.id })
      .then((result) => {
        if (isSuccessfulResult(result)) {
          setTrackerToRemove(undefined);
        }
      })
      .catch(console.error);
  }, [accountId, deleteTracker, trackerToRemove]);

  if (error !== undefined) {
    return <ErrorView error={error} retry={retry} />;
  }
  if (isLoading || data === undefined) {
    return <LoadingView />;
  }

  const providerMark = trackerToRemove === undefined ? undefined : trackerProviderMark(trackerToRemove.type);
  const removedProviderName = providerMark?.name ?? '';

  return (
    <>
      <TrackersPanel trackers={data.trackers} onAdd={openAdd} onRemove={onRemove} />
      <AddTrackerModal isOpen={isAddOpen} accountId={accountId} closeModal={closeAdd} />
      <ConfirmDialog
        isOpen={trackerToRemove !== undefined}
        tone="danger"
        mark={
          providerMark === undefined ? (
            <ConfirmDialogIconMark tone="danger" icon={faTrash} />
          ) : (
            <ConfirmDialogTileMark background={providerMark.color} withRemoveBadge>
              <FontAwesomeIcon icon={providerMark.icon} />
            </ConfirmDialogTileMark>
          )
        }
        title={intl.formatMessage({ id: 'app.sml.trackers.remove.title', defaultMessage: 'Remove this tracker?' })}
        description={intl.formatMessage(
          {
            id: 'app.sml.trackers.remove.description',
            defaultMessage: '{provider} stops loading on every smartlink page in your account.',
          },
          { provider: removedProviderName }
        )}
        confirmLabel={intl.formatMessage({ id: 'app.sml.trackers.remove.confirm', defaultMessage: 'Remove' })}
        isBusy={isDeleting}
        error={deleteError === undefined ? undefined : errorAlertMessage(intl, deleteError)}
        onConfirm={confirmRemove}
        onCancel={closeRemove}
        testId="tracker-remove-dialog"
      />
    </>
  );
};

export const TrackersSection = TypedMemo(TrackersSectionComponent);
